'use client'

/**
 * §19.10 — "the undo stack is the same log filtered to undoable === true,
 * without timestamps, capped at the last 20." Rendered here as the second
 * view onto the one session-log structure (components/create/session-log.ts);
 * the activity rail is the other.
 */

import { cn } from '@/lib/utils'
import type { UseSessionLog } from './session-log'

interface UndoHistoryProps {
  sessionLog: UseSessionLog
  className?: string
}

export function UndoHistory({ sessionLog, className }: UndoHistoryProps) {
  const { undoStack, redoDepth, canUndo, canRedo, undo, redo } = sessionLog

  if (undoStack.length === 0) return null

  const activeIndex = undoStack.length - 1 - redoDepth

  return (
    // No bordered/filled box (Rule 1 — no card): a top hairline, like the
    // other control clusters on this page.
    <div className={cn('border-t border-[color:var(--tls-border-subtle)] pt-4', className)}>
      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={undo}
          disabled={!canUndo}
          className="tlsr-focus min-h-[44px] border border-[color:var(--tls-border-strong)] px-4 text-sm font-medium text-[var(--tls-fg-strong)] disabled:opacity-40"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={!canRedo}
          className="tlsr-focus min-h-[44px] border border-[color:var(--tls-border-strong)] px-4 text-sm font-medium text-[var(--tls-fg-strong)] disabled:opacity-40"
        >
          Redo
        </button>
      </div>
      <ol className="flex flex-col">
        {undoStack.map((record, i) => {
          const isActive = i === activeIndex
          const isUndone = i > activeIndex
          return (
            <li
              key={record.entryId}
              className={cn(
                'grid grid-cols-[16px_1fr_auto] items-center gap-3 border-b border-[color:var(--tls-border-subtle)] py-2 text-sm last:border-b-0',
                isUndone && 'opacity-45'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'size-2 rounded-full',
                  isActive
                    ? 'bg-[var(--tls-brand)]'
                    : isUndone
                      ? 'border border-[color:var(--tls-border-strong)]'
                      : 'bg-[var(--tls-fg-strong)]'
                )}
              />
              <span className={cn('truncate text-[var(--tls-fg-strong)]', isActive ? 'font-semibold' : 'font-medium')}>
                {record.label}
              </span>
              {isActive && (
                <span className="tls-eyebrow [--tls-eyebrow-color:var(--tls-fg-strong)]">
                  Now
                </span>
              )}
            </li>
          )
        })}
      </ol>
      <p className="mt-3 text-xs leading-relaxed text-[var(--tls-fg-subtle)]">
        Up to {20} steps. Redoable steps stay visible, dimmed, so you can see what you&apos;re redoing. A new edit
        after an undo discards them and says so in the activity log.
      </p>
    </div>
  )
}
